import {minZero} from '@shared/helpers/lang'

import {MaximumUsingChargePointAmount, PayMethodType, PreferredPoint, PreferredPointType} from '@web/core/constants'

import {getRechargeablePoint, isRechargePointEnabled} from './rechargePoint'

/**
 * 사용될 페이포인트 (적립포인트)
 * - 페이포인트가 조회 실패되었다면, rewardPointAmount 는 0원
 **/
export function getRewardPointAmountToBeUsed({
    usingPointAmountTotal,
    rewardPointAmount,
}: {
    /** 사용될 pay point 총 합 */
    usingPointAmountTotal: number
    /** 보유 페이포인트 (적립포인트) */
    rewardPointAmount: number
}) {
    return minZero(Math.min(usingPointAmountTotal, rewardPointAmount))
}

/**
 * 사용될 페이머니 (충전포인트)
 * - 페이머니가 조회 실패되었다면, chargePointAmount 는 0원
 * - 페이머니 연결계좌가 점검중이라면, chargePointAmount 는 0원(?)
 **/
export function getUsingChargePointAmount({
    paymentAmount,
    usingPointAmountTotal,
    usingRewardPointAmount,
    usingPayLaterAmount,
    chargePointAmount,
    pointRechargingEnabled,
    pointChargingAccountExists,
    payMoneyStateAvailable,
    isPayMoneyPolicy,
}: {
    /** 결제금액 */
    paymentAmount: number
    /**
     * 사용된 포인트
     * - 페이머니 가입자 : (페이포인트)
     * - 페이머니 미가입자 : (페이포인트 + 페이머니)
     **/
    usingPointAmountTotal: number
    /** 사용된 페이포인트 금액 */
    usingRewardPointAmount: number
    /** 사용된 후불결제 금액 */
    usingPayLaterAmount: number
    /** 보유 페이머니 */
    chargePointAmount: number
    /** 충전결제 사용가능여부 */
    pointRechargingEnabled: boolean
    /** 페이머니 유저 여부 */
    payMoneyStateAvailable: boolean
    /** 페이머니 계좌 결제 가능 여부 */
    pointChargingAccountExists: boolean
    /** 충전포인트 페이머니취급정책 여부 */
    isPayMoneyPolicy: boolean
}) {
    if (!payMoneyStateAvailable && !isPayMoneyPolicy) {
        return minZero(usingPointAmountTotal - usingRewardPointAmount)
    }
    if (
        /**
         * 1) 충전결제 사용 & (페이머니계좌연결된 페이머니 유저 or 페이머니 정책적용)
         * 2) 1.0 유저가 후불결제 사용 시 충전결제는 사용이 불가능하지만 보유머니는 사용가능하여 사용충전포인트 계산해야하는 case
         **/
        (pointRechargingEnabled && (pointChargingAccountExists || isPayMoneyPolicy)) ||
        (!payMoneyStateAvailable && isPayMoneyPolicy && usingPayLaterAmount > 0)
    ) {
        return minZero(
            Math.min(
                paymentAmount - usingRewardPointAmount - usingPayLaterAmount,
                Math.min(chargePointAmount, MaximumUsingChargePointAmount),
            ),
        )
    }
    return 0
}

/**
 * 신규 포인트 소진 정책
 * - 포인트 소진 순서 반영
 */
export function getPointAmountByPreferredPointPolicy({
    preferredPointType,
    usingPointAmountTotal,
    rewardPointAmount,
    cashRewardPointAmount,
    chargePointAmount,
    paymentAmount,
    usingPayLaterAmount,
    pointRechargingEnabled,
    payMoneyStateAvailable,
    pointChargingAccountExists,
    isPayMoneyPolicy,
}: {
    /** 포인트 우선 사용 순서 */
    preferredPointType: PreferredPointType
    /**
     * 사용된 포인트
     * - 페이머니 가입자 : (페이포인트)
     * - 페이머니 미가입자 : (페이포인트 + 페이머니)
     **/
    usingPointAmountTotal: number
    /** 보유 페이포인트 (적립포인트) */
    rewardPointAmount: number
    /** 보유 현금성 페이포인트 (적립포인트 내 분류) */
    cashRewardPointAmount: number
    /** 보유 페이머니 */
    chargePointAmount: number
    /** 결제금액 */
    paymentAmount: number
    /** 사용된 후불결제 금액 */
    usingPayLaterAmount: number
    /** 충전결제 사용가능여부 */
    pointRechargingEnabled: boolean
    /** 페이머니 유저 여부 */
    payMoneyStateAvailable: boolean
    /** 페이머니 계좌 결제 가능 여부 */
    pointChargingAccountExists: boolean
    /** 충전포인트 페이머니취급정책 여부 */
    isPayMoneyPolicy: boolean
}): {
    usingRewardPointAmount: number
    usingChargePointAmount: number
    usingCashRewardPointAmount: number
} {
    const usingRewardPointAmount =
        preferredPointType === PreferredPoint.REWARD_POINT || payMoneyStateAvailable
            ? getRewardPointAmountToBeUsed({
                  usingPointAmountTotal,
                  rewardPointAmount,
              })
            : minZero(usingPointAmountTotal - chargePointAmount)
    const usingChargePointAmount = getUsingChargePointAmount({
        payMoneyStateAvailable,
        usingPointAmountTotal,
        usingRewardPointAmount,
        paymentAmount,
        usingPayLaterAmount,
        chargePointAmount,
        pointRechargingEnabled,
        pointChargingAccountExists,
        isPayMoneyPolicy,
    })
    /**
     * case 1) 사용적립포인트 > 보유 현금성포인트
     * -> 사용 현금포인트는 보유 현금성포인트 전부
     * case 2) 사용적립포인트 <= 보유 현금성포인트
     * -> 사용 현금포인트는 사용적립포인트 금액과 같음
     */
    const usingCashRewardPointAmount =
        usingRewardPointAmount > cashRewardPointAmount ? cashRewardPointAmount : usingRewardPointAmount
    return {
        usingRewardPointAmount,
        usingChargePointAmount,
        usingCashRewardPointAmount,
    }
}

/**
 * 충전후 사용될 페이머니 (충전포인트)
 * - 페이머니 가입자의 충전금은 200만원까지 가능하다
 * - 페이머니 미가입자의 충전금은 잔여 충전금을 넘을 수 없다
 **/
export const getUsingRechargePointAmount = ({
    paymentAmount,
    usingRewardPointAmount,
    usingChargePointAmount,
    usingPayLaterAmount = 0,
    rechargePointAmount,
    payMoneyStateAvailable,
}: {
    paymentAmount: number
    /** 사용된 페이포인트 금액 */
    usingRewardPointAmount: number
    /** 사용된 페이머니 금액 */
    usingChargePointAmount: number
    /** 사용된 후불결제 금액 */
    usingPayLaterAmount?: number
    /** 충전 포인트 */
    rechargePointAmount: number
    /** 페이머니 사용가능 여부 */
    payMoneyStateAvailable: boolean
}) => {
    const remainPaymentAmount = paymentAmount - usingRewardPointAmount - usingChargePointAmount
    const remainAmount = payMoneyStateAvailable
        ? Math.min(remainPaymentAmount - usingPayLaterAmount, MaximumUsingChargePointAmount - usingChargePointAmount)
        : remainPaymentAmount
    return minZero(Math.min(rechargePointAmount, remainAmount))
}

/** 충전될 페이머니 */
export const getRechargePointAmount = ({
    orderAmount,
    usingRewardPointAmount = 0,
    usingChargePointAmount = 0,
    usingPayLaterAmount = 0,
    availableMethod,
    chargePointAmount,
    rechargePoint,
    payLaterUsed = false,
    payMoneyStateAvailable = false,
}: {
    /** 결제할 금액 */
    orderAmount: number
    /** 사용하는 페이 포인트 금액 */
    usingRewardPointAmount?: number
    /** 사용하는 페이 머니 금액 */
    usingChargePointAmount?: number
    /** 사용하는 후불결제 금액 */
    usingPayLaterAmount?: number
    /** 충전결제 수단 사용가능 여부 */
    availableMethod: boolean
    /** 페이머니 잔액 */
    chargePointAmount: number
    rechargePoint?: {
        enabled?: boolean
        /** true 로 입력된다면, 보유 포인트에 따라 최대 충전금액 반영 */
        usePointAll?: boolean
        rechargingAmount: number
        /** 잔여 충전 가능 잔액 */
        reloadableRechargePointAmount: number
        /** 충전계좌 결제 가능 여부 (전체계좌가 점검중, 선택한 계좌가 점검중이라면 불가능) */
        canUseRechargeAccount: boolean
        /** 페이머니 계좌 결제 가능 여부 */
        canUseChargePointAccount?: boolean
    }
    /** 후불결제 사용 여부 */
    payLaterUsed?: boolean
    /** 페이머니 가입자 여부 */
    payMoneyStateAvailable?: boolean
}) => {
    const remainAmount = orderAmount - usingRewardPointAmount - usingChargePointAmount - usingPayLaterAmount

    if (
        rechargePoint &&
        rechargePoint.enabled &&
        availableMethod &&
        isRechargePointEnabled({
            reloadableRechargePointAmount: rechargePoint.reloadableRechargePointAmount,
            remainOrderAmount: remainAmount,
            payLaterUsed,
            canUseRechargeAccount: rechargePoint.canUseRechargeAccount,
            canUseChargePointAccount: rechargePoint.canUseChargePointAccount,
            payMoneyStateAvailable,
        })
    ) {
        /** 페이머니 + 충전후 200만원 까지만 가능하다. */
        const usingAllRechargePointAmount = Math.min(
            remainAmount,
            MaximumUsingChargePointAmount - usingChargePointAmount,
        )
        return getRechargeablePoint({
            /** 최대로 충전할 수 있는 충전 포인트 */
            maximumRechargingAmount: rechargePoint.reloadableRechargePointAmount,
            /** 최소 충전할 수 있는 충전 포인트 */
            /** 페이머니 유저는 잔액이 없다면 최소 1만원 이상 충전해야 한다. */
            minimumRechargingAmount: payMoneyStateAvailable && chargePointAmount > 0 ? 0 : 10000,
            rechargePoint: rechargePoint.usePointAll
                ? usingAllRechargePointAmount
                : (rechargePoint.rechargingAmount ?? 0),
        })
    }
    return 0
}

/** 페이머니 사용시 필요 정보 state 갖추어졌는지 여부 */
export const getPayMoneyPaymentPreparationInfo = ({
    generalPaymentMethodType,
    paymentAmountForGeneralPayment,
    chargingPointIsUsed,
    rechargingPayMoneyIsUsed,
    chargingPointInfoIsLoaded,
    payMoneyInfoIsLoaded,
    payMoneyStateAvailable,
    payMoneyBankAccountIsStopped,
    payMoneyAccountExists,
    payMoneyTransferIsFailed,
}: {
    /** 일반결제 타입 */
    generalPaymentMethodType: Maybe<PayMethodType>
    /** 일반결제 잔여결제금액 */
    paymentAmountForGeneralPayment: number
    /** 페이머니 사용여부 */
    chargingPointIsUsed: boolean
    /** 페이머니 충전결제 사용여부 */
    rechargingPayMoneyIsUsed: boolean
    /** 충전 포인트 정보 로드 여부 */
    chargingPointInfoIsLoaded: boolean
    /** 페이머니 정보 로드 성공 여부 */
    payMoneyInfoIsLoaded: boolean
    /** 페이머니 계좌 결제 사용 가능여부 */
    payMoneyStateAvailable: boolean
    /** 페이머니 계좌 존재여부 */
    payMoneyAccountExists: boolean
    /** 페이머니 통장 거래중지 여부 */
    payMoneyBankAccountIsStopped: boolean
    /** 페이머니 계좌 잔액이체 실패 여부 */
    payMoneyTransferIsFailed: boolean
}) => {
    // 페이머니 or 페이머니 충전결제를 사용으로 남은 금액이 없거나, 페이머니를 사용했거나, 페이머니 충전결제를 사용할때
    const payMoneyIsUsed =
        (!generalPaymentMethodType && paymentAmountForGeneralPayment > 0) ||
        chargingPointIsUsed ||
        rechargingPayMoneyIsUsed

    // 페이머니 결제를 위해 필요한 정보가 로드되지 않은 상태
    const essentialInfoIsNotLoaded = !chargingPointInfoIsLoaded || !payMoneyInfoIsLoaded
    // 페이머니가 결제 불가능한 상태라면
    const payMoneyPaymentNotAvailable = !payMoneyStateAvailable || essentialInfoIsNotLoaded
    // 페이머니 계좌가 삭제되었거나 개설되지 않은 상태
    const payMoneyBankAccountIsNeeded = payMoneyPaymentNotAvailable && !payMoneyAccountExists
    // 페이머니 계좌가 페이머니 결제를 위해 준비된 상태가 아닐 때
    const payMoneyAccountStateNotReady =
        payMoneyPaymentNotAvailable && (payMoneyTransferIsFailed || payMoneyBankAccountIsStopped)

    return {
        payMoneyIsUsed,
        payMoneyBankAccountIsNeeded,
        payMoneyAccountStateNotReady,
        essentialInfoIsNotLoaded,
    }
}
